import { usePageNavigation } from '@/contexts/PageNavigationContext';
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Lightning, Crown, Rocket, ArrowsClockwise, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const TIER_ICON = {
  starter: <Lightning size={28} weight="duotone" />,
  pro: <Rocket size={28} weight="duotone" />,
  enterprise: <Crown size={28} weight="duotone" />,
};

const TIER_KLEUR = {
  starter: 'from-sky-50 to-sky-100 border-sky-300',
  pro: 'from-violet-50 to-violet-100 border-violet-400 ring-2 ring-violet-300',
  enterprise: 'from-amber-50 to-amber-100 border-amber-300',
};

const TIER_BUTTON = {
  starter: 'bg-sky-600 hover:bg-sky-700',
  pro: 'bg-violet-600 hover:bg-violet-700',
  enterprise: 'bg-amber-600 hover:bg-amber-700',
};

/**
 * SaaS Abonnementspagina (BillFac).
 *
 * - Toont 3 tiers (Starter / Pro / Enterprise) × 2 intervals (maand / jaar)
 * - Bij klik op "Upgrade" → POST /api/saas/checkout → Stripe Checkout
 * - Bij terugkeer via session_id → polling /api/saas/status/{session_id}
 * - Toont huidige actieve subscription bovenaan
 */
export default function AbonnementPage() {
  const { goBack } = usePageNavigation();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionIdInUrl = params.get('session_id');
  const cancelled = params.get('cancelled');

  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState('maand'); // 'maand' | 'jaar'
  const [loading, setLoading] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pollMsg, setPollMsg] = useState('');

  // Laad plans + huidige tenant-status
  useEffect(() => {
    (async () => {
      try {
        const [p, t] = await Promise.all([
          API.get('/saas/plans'),
          API.get('/tenant/me'),
        ]);
        setPlans(p.data?.plans || []);
        setActivePlan(t.data || null);
      } catch (e) {
        toast.error('Kon abonnementsgegevens niet laden');
      }
    })();
  }, []);

  // Polling-flow: indien session_id in URL → check status
  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    const MAX = 10;
    const INTERVAL_MS = 1800;
    if (attempts >= MAX) {
      setPolling(false);
      setPollMsg('Status-controle duurt langer dan verwacht. Vernieuw deze pagina om te checken of je abonnement actief is.');
      return;
    }
    try {
      const r = await API.get(`/saas/status/${sessionId}`);
      const ps = r.data?.payment_status;
      if (ps === 'paid') {
        setPolling(false);
        setPollMsg('');
        toast.success('Betaling succesvol! Je abonnement is geactiveerd 🎉');
        // Refresh tenant status
        const t = await API.get('/tenant/me');
        setActivePlan(t.data || null);
        // URL opschonen
        const next = new URLSearchParams(params);
        next.delete('session_id');
        setParams(next, { replace: true });
        return;
      }
      if (r.data?.status === 'expired') {
        setPolling(false);
        setPollMsg('De betaalsessie is verlopen. Probeer opnieuw.');
        return;
      }
      // Continue polling
      setPollMsg(`Betaling wordt verwerkt... (${attempts + 1}/${MAX})`);
      setTimeout(() => pollStatus(sessionId, attempts + 1), INTERVAL_MS);
    } catch (e) {
      setPolling(false);
      setPollMsg('Fout bij status-controle. Vernieuw de pagina.');
    }
  }, [params, setParams]);

  useEffect(() => {
    if (sessionIdInUrl) {
      setPolling(true);
      setPollMsg('Bevestigen van betaling...');
      pollStatus(sessionIdInUrl);
    }
    if (cancelled) {
      toast.info('Betaling geannuleerd. Je abonnement is niet gewijzigd.');
      const next = new URLSearchParams(params);
      next.delete('cancelled');
      setParams(next, { replace: true });
    }
  }, [sessionIdInUrl, cancelled, params, pollStatus, setParams]);

  const handleUpgrade = async (planId) => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const r = await API.post('/saas/checkout', { plan_id: planId, origin });
      const url = r.data?.url;
      if (!url) throw new Error('Geen checkout-URL ontvangen');
      // Volledige page redirect naar Stripe
      window.location.href = url;
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Checkout mislukt');
      setLoading(false);
    }
  };

  // Filter plans per interval
  const visiblePlans = plans.filter(p => p.interval === interval);

  const isActief = activePlan?.subscription_expires_at && activePlan?.subscription_dagen_over > 0;
  const isTrial = activePlan?.is_trial;
  const trialOver = activePlan?.trial_dagen_over;

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8" data-testid="abonnement-page">
      <div className="text-center space-y-2">
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Kies je BillFac-abonnement</h1>
        <p className="text-slate-600 max-w-2xl mx-auto">
          Alle abonnementen geven onbeperkte facturatie, AI-PDF parsing en email-koppeling. Schaal mee met je team.
        </p>
      </div>

      {/* Polling-banner */}
      {polling && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3" data-testid="polling-banner">
          <ArrowsClockwise size={24} className="animate-spin text-blue-600" />
          <span className="text-blue-900 font-medium">{pollMsg}</span>
        </div>
      )}

      {/* Huidige status */}
      {activePlan && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="huidige-status-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Huidige status</p>
              <p className="text-xl font-bold text-slate-900 mt-1" data-testid="huidige-plan-naam">
                {isActief ? `${(activePlan.plan_tier || 'Pro').toUpperCase()} — actief tot ${new Date(activePlan.subscription_expires_at).toLocaleDateString('nl-BE')}`
                  : isTrial ? `Trial — nog ${trialOver ?? 0} dag(en)`
                  : activePlan.plan_tier === 'lifetime' ? 'Lifetime — geen verlenging nodig'
                  : 'Geen actief abonnement'}
              </p>
            </div>
            {isActief && (
              <Badge className="bg-green-600 text-white text-sm" data-testid="actief-badge">
                <CheckCircle weight="fill" className="mr-1" size={14}/> Actief
              </Badge>
            )}
            {isTrial && (
              <Badge className="bg-amber-500 text-white text-sm" data-testid="trial-badge">
                Trial verloopt binnen {trialOver ?? 0}d
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Interval toggle */}
      <div className="flex justify-center">
        <div className="inline-flex bg-slate-100 rounded-full p-1" data-testid="interval-toggle">
          <button
            onClick={() => setInterval('maand')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${interval === 'maand' ? 'bg-white text-slate-900 shadow' : 'text-slate-600'}`}
            data-testid="toggle-maand"
          >
            Maandelijks
          </button>
          <button
            onClick={() => setInterval('jaar')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${interval === 'jaar' ? 'bg-white text-slate-900 shadow' : 'text-slate-600'}`}
            data-testid="toggle-jaar"
          >
            Jaarlijks <Badge className="ml-1 bg-green-500 text-white text-xs">2 mnd gratis</Badge>
          </button>
        </div>
      </div>

      {/* Pricing cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {visiblePlans.map(plan => (
          <div
            key={plan.id}
            className={`relative bg-gradient-to-br ${TIER_KLEUR[plan.tier]} border-2 rounded-2xl p-6 flex flex-col shadow-sm hover:shadow-lg transition-shadow`}
            data-testid={`plan-card-${plan.id}`}
          >
            {plan.tier === 'pro' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-violet-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                MEEST GEKOZEN
              </div>
            )}
            <div className="flex items-center gap-2 mb-2 text-slate-700">
              {TIER_ICON[plan.tier]}
              <h3 className="text-xl font-bold text-slate-900 capitalize">{plan.tier}</h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">{plan.label}</p>
            <div className="mb-6">
              <span className="text-4xl font-bold text-slate-900">€{plan.amount.toFixed(0)}</span>
              <span className="text-slate-500 ml-1">/ {plan.interval}</span>
            </div>
            <ul className="space-y-2 flex-1 mb-6">
              {(plan.features || []).map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle weight="fill" className="text-green-600 mt-0.5 flex-shrink-0" size={16} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button
              onClick={() => handleUpgrade(plan.id)}
              disabled={loading || polling}
              className={`w-full text-white ${TIER_BUTTON[plan.tier]}`}
              data-testid={`upgrade-btn-${plan.id}`}
            >
              {loading ? 'Bezig...' : (isActief && activePlan?.subscription_plan === plan.id ? 'Verlengen' : 'Upgrade nu')}
            </Button>
          </div>
        ))}
      </div>

      <div className="text-center text-xs text-slate-400 pt-6">
        Veilige betaling via Stripe · Eenmalige betaling activeert je tier voor {interval === 'maand' ? '31' : '366'} dagen ·
        Bij verlenging wordt de duur opgeteld bij je huidige einddatum
      </div>

      <div className="text-center">
        <button
          type="button" onClick={() => goBack()}
          className="text-slate-500 hover:text-slate-700 text-sm inline-flex items-center gap-1"
          data-testid="terug-btn"
        >
          <X size={14}/> Sluiten
        </button>
      </div>

      {/* ─── GDPR Account Beheer ─────────────────────────────── */}
      <GdprPanel />
    </div>
  );
}

// ─── GDPR Self-service Export + Account Deletion ────────────────────────────
function GdprPanel() {
  const [busy, setBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [conf, setConf] = useState('');
  const navigate = useNavigate();

  const handleExport = async () => {
    setBusy(true);
    try {
      // Stream JSON-download
      const resp = await API.get('/tenant/gdpr-export', { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `billfac-gdpr-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('GDPR-export gedownload');
    } catch (e) {
      toast.error('Export mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
  };

  const handleSelfDelete = async () => {
    if (conf !== 'VERWIJDER MIJN ACCOUNT') {
      toast.error('Bevestigingstekst klopt niet exact');
      return;
    }
    setBusy(true);
    try {
      await API.post('/tenant/self-delete', { confirmation: conf });
      toast.success('Je account is permanent verwijderd');
      // Force logout — redirect naar login (verwijder ook tokens)
      try { localStorage.clear(); } catch (_err) { /* ignore */ }
      setTimeout(() => { window.location.href = '/login'; }, 800);
    } catch (e) {
      toast.error('Verwijderen mislukt: ' + (e.response?.data?.detail || e.message));
      setBusy(false);
    }
  };

  return (
    <div className="mt-12 pt-8 border-t border-slate-200 space-y-6" data-testid="gdpr-panel">
      <div>
        <h2 className="text-xl font-bold text-slate-900">Account-beheer & GDPR</h2>
        <p className="text-sm text-slate-500 mt-1">
          Volgens GDPR Art. 17 (Recht op vergetelheid) en Art. 20 (Data-portabiliteit).
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Export */}
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="gdpr-export-card">
          <h3 className="font-semibold text-slate-900 mb-2">Exporteer mijn data</h3>
          <p className="text-sm text-slate-600 mb-4">
            Download <strong>alle</strong> data van je account (klanten, artikels, facturen, mails, agenda...) als één JSON-bestand.
          </p>
          <Button
            onClick={handleExport}
            disabled={busy}
            className="bg-slate-700 hover:bg-slate-800 text-white"
            data-testid="gdpr-export-btn"
          >
            {busy ? 'Bezig...' : 'Download GDPR-export'}
          </Button>
        </div>

        {/* Self-delete */}
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-5" data-testid="gdpr-delete-card">
          <h3 className="font-semibold text-red-900 mb-2">Verwijder mijn account</h3>
          <p className="text-sm text-red-800 mb-4">
            <strong>Onomkeerbaar.</strong> Alle bedrijfsdata wordt permanent gewist.
            Bevestiging vereist.
          </p>
          {!showDelete ? (
            <Button
              onClick={() => setShowDelete(true)}
              variant="outline"
              className="border-red-500 text-red-700 hover:bg-red-100"
              data-testid="gdpr-delete-toggle-btn"
            >
              Open verwijdering...
            </Button>
          ) : (
            <div className="space-y-3">
              <input
                type="text"
                placeholder='Typ exact: VERWIJDER MIJN ACCOUNT'
                value={conf}
                onChange={e => setConf(e.target.value)}
                className="w-full px-3 py-2 border border-red-300 rounded text-sm"
                data-testid="gdpr-delete-confirm-input"
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleSelfDelete}
                  disabled={busy || conf !== 'VERWIJDER MIJN ACCOUNT'}
                  className="bg-red-600 hover:bg-red-700 text-white flex-1"
                  data-testid="gdpr-delete-confirm-btn"
                >
                  {busy ? 'Bezig...' : 'Verwijder permanent'}
                </Button>
                <Button
                  onClick={() => { setShowDelete(false); setConf(''); }}
                  variant="outline"
                  data-testid="gdpr-delete-cancel-btn"
                >
                  Annuleren
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
